import type React from "react";
import { useState } from "react";

import PaginationTabs from "@/components/common/navigation/pagination-tabs.tsx";
import { boxclass, cn, renderMarkdown } from "@/helpers";
import type { Candidate, LogEntry } from "@/types/candidate";

interface LogQna {
  question: string;
  answer: string | null;
  order: number;
}

interface RenderLogProps {
  title?: string;
  log: LogEntry;
  salary: number | null;
}

export default function Logs({ candidate }: { candidate: Candidate }) {
  const [active_log_idx, setActiveLogIndex] = useState(0);

  const { log: logs, salary } = candidate;

  // The active index is state, so it can outlive the list it points into.
  const active_log = logs[active_log_idx];
  const only_log = logs[0];

  return (
    <div className="mx-auto mb-8 w-[40rem]">
      <Section>
        {logs.length > 1 && active_log && (
          <div>
            <PaginationTabs
              active_option_index={active_log_idx}
              setActiveOptionIndex={setActiveLogIndex}
              options={logs.map((log, idx) => ({
                id: idx,
                text: log.description,
              }))}
            />
            <RenderLog log={active_log} salary={salary} />
          </div>
        )}
        {logs.length === 1 && only_log && (
          <RenderLog title={only_log.description} log={only_log} salary={salary} />
        )}
      </Section>
    </div>
  );
}

const Section = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className={cn(boxclass, "p-8")}>
      <div className="overflow-hidden">{children}</div>
    </div>
  );
};

// Structured logs are JSON, the rest are plain markdown, so a parse failure is a
// routing signal rather than an error.
const parseLogData = (log_text: string): Record<string, LogQna> | null => {
  try {
    return JSON.parse(log_text.replace("\n", "")) as Record<string, LogQna>;
  } catch {
    return null;
  }
};

const RenderLog = ({ title, log, salary }: RenderLogProps) => {
  let title_element: React.ReactNode | null = null;
  if (title) {
    title_element = (
      <div className="text-primary mb-4 flex items-center gap-4 rounded bg-slate-800 px-2 py-1">
        {title}
      </div>
    );
  }
  const log_data = parseLogData(log.log_text);
  let content = <div className="flex justify-center">Empty</div>;
  if (log_data) {
    content = (
      <div className="content">
        {title_element}
        {Object.values(log_data)
          .filter((entry) => entry.question)
          .toSorted((a, b) => (a.order < b.order ? -1 : 1))
          .map((qna, i) => {
            return (
              <div key={i}>
                <h2>{qna.question}</h2>
                {qna.answer ? (
                  <div dangerouslySetInnerHTML={renderMarkdown(qna.answer)} />
                ) : (
                  <div className="flex justify-center">Empty</div>
                )}
              </div>
            );
          })}
      </div>
    );
  } else if (log.log_text) {
    content = (
      <div className="content">
        {title_element}
        <div dangerouslySetInnerHTML={renderMarkdown(log.log_text)} />
      </div>
    );
  }
  return (
    <div className="relative">
      {salary && log.base_salary && (
        <div className="absolute top-8 right-8 flex items-center gap-1 opacity-75" title="Salary">
          <span className="material-symbols-rounded notranslate">paid</span>{" "}
          {log.base_salary.toLocaleString()}
        </div>
      )}
      {content}
    </div>
  );
};
